import { useState } from 'react'
import { Button, Group, Loader, Modal, Select, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

type Props = {
  patientId: string
  patientName: string
  opened: boolean
  onClose: () => void
  onAssigned: () => void
}

export const AssignOrganizationModal = ({
  patientId,
  patientName,
  opened,
  onClose,
  onAssigned,
}: Props) => {
  const orpc = useOrpc()
  const [organizationId, setOrganizationId] = useState<string | null>(null)

  const eligible = useQuery(
    orpc.listUnmatchedPatientOrganizations.queryOptions({
      input: { patientId },
      enabled: opened,
    }),
  )

  const assign = useMutation(
    orpc.assignUnmatchedPatient.mutationOptions({
      onSuccess: () => {
        showNotification({ color: 'green', message: 'Patient assigned to the organization' })
        onAssigned()
      },
      onError: (error) => {
        logError('patient', 'Assign unmatched patient', error)
        showNotification({
          color: 'red',
          message: 'Failed to assign the patient. Please try again.',
        })
      },
    }),
  )

  const organizations = eligible.data?.organizations ?? []
  // A background refresh can drop the chosen organization; assign only one still listed.
  const selectable =
    eligible.isSuccess && organizations.some((organization) => organization.id === organizationId)

  return (
    <Modal opened={opened} onClose={onClose} title="Assign to organization">
      <Text size="sm" mb="md">
        {patientName} has no provider licensed in their state
        {eligible.data ? ` (${eligible.data.state})` : ''}. Choose an organization with a provider
        licensed there.
      </Text>
      {eligible.isPending ? (
        <Loader size="sm" />
      ) : eligible.isError ? (
        <Text size="sm" c="red">
          Failed to load the organizations.
        </Text>
      ) : organizations.length === 0 ? (
        <Text size="sm">No organization has a provider licensed in this state yet.</Text>
      ) : (
        <Select
          label="Organization"
          placeholder="Select an organization"
          data={organizations.map((organization) => ({
            value: organization.id,
            label: organization.name,
          }))}
          value={organizationId}
          onChange={setOrganizationId}
          searchable
        />
      )}
      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="red"
          disabled={!selectable}
          loading={assign.isPending}
          onClick={() =>
            selectable && organizationId && assign.mutate({ patientId, organizationId })
          }
        >
          Assign
        </Button>
      </Group>
    </Modal>
  )
}
